import React from "react";
import classnames from "classnames";
import { getCypressTestId } from "../../../utils/config";
import { injectIntl, IntlShape } from "react-intl";
import IntlMessages from "../../../utils/messages";

interface Props {
  intl: IntlShape;
  item: { id: string } | null | undefined;
  id: string;
  name: string;
  type: string;
  value: number;
  label: string;
  placeholder?: string;
  index: number;
  last?: boolean;
  required?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  children?: React.ReactNode;
  onChange: (e) => void;
  onBlur?: (e) => void;
  errors: any;
  touched: any;
  grid?: number;
  step?: number;
  min?: number;
  max?: number;
  className?: string;
  action?: React.ReactNode;
  useIntl: boolean;
  description?: string;
  alternate?: boolean;
}

const NumberInput = (props: Props): JSX.Element => {
  const {
    grid,
    className,
    last,
    errors,
    name,
    touched,
    action,
    index,
    useIntl,
    label,
    required,
    id,
    item,
    type,
    min,
    max,
    step,
    placeholder,
    intl,
    value,
    onChange,
    onBlur,
    disabled,
    alternate,
  } = props;

  const alter = alternate != undefined ? alternate : true;

  const clazz = grid ? `sm:grid-cols-${grid}` : "sm:grid-cols-3";

  const containerClassName = id =>
    classnames(
      "sm:grid sm:gap-4 sm:items-start sm:py-2 sm:px-8 px-2 py-2 " + clazz,
      className,
      {
        "bg-ground-white-200": alter && id % 2 === 0,
        "rounded-b-10": last,
      }
    );

  const invalid = errors[name] && touched[name];

  const formClassName = classnames(
    "form-input block w-full transition duration-150 ease-in-out sm:text-14px sm:leading-5 placeholder-ground-gray-100",
    {
      "border-red-300 text-red-500 placeholder-red-300 focus:border-red-300 focus:shadow-outline-red":
        invalid,
      "text-ground-black-100 placeholder-ground-black-100": !invalid,
    }
  );

  const labelClassName = classnames(
    "block text-14px font-medium leading-5 text-ground-gray-100 sm:mt-px sm:pt-2 ",
    {
      "sm:col-span-2": grid == 4,
    }
  );

  const inputClassName = classnames("mt-1 sm:mt-0 max-w-xs", {
    "sm:col-span-2": !action,
  });

  return (
    <div className={containerClassName(index)}>
      <label htmlFor={name} className={labelClassName}>
        {useIntl && <IntlMessages id={label} />}
        {!useIntl && `${label}`} {required && "*"}
      </label>
      <div className={inputClassName}>
        <div className="relative rounded-md shadow-sm">
          <input
            key={`${name}_${index}`}
            className={formClassName}
            id={id}
            data-cy={name}
            data-testid={getCypressTestId(item)}
            name={name}
            type={type}
            min={min ? min : 0}
            max={max}
            step={step ? step : 1}
            placeholder={
              useIntl
                ? intl.formatMessage({ id: `${placeholder}` })
                : placeholder
            }
            value={value}
            disabled={disabled}
            onChange={onChange}
            onBlur={onBlur}
          />
          {invalid && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <svg
                className="h-5 w-5 text-red-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          )}
        </div>
        {invalid && (
          <p className="mt-2 text-11px text-red-600" id={`${name}-error`}>
            {errors[name]}
          </p>
        )}
      </div>
      {action && <div className="my-auto">{action}</div>}
    </div>
  );
};

export default injectIntl(NumberInput);
